
// src/features/lottery/utils.js
import { db } from "../../firebase";
import {
    collection,
    doc,
    query,
    where,
    orderBy,
    limit,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    onSnapshot,
    serverTimestamp,
    runTransaction,
} from "firebase/firestore";

/* -------------------------------------------------------------------------- */
/*                               Time utilities                               */
/* -------------------------------------------------------------------------- */

// Next 9:00 PM IST as a JS Date (Firestore will store as Timestamp)
export function getNextISTDrawDate(fromDate = new Date()) {
    const IST_OFFSET_MIN = 330; // +5:30, no DST
    const nowUtcMs = fromDate.getTime();
    const nowIstMs = nowUtcMs + IST_OFFSET_MIN * 60 * 1000; // shift to IST wall-time
    const nowIst = new Date(nowIstMs);

    const targetIst = new Date(nowIst);
    targetIst.setHours(21, 0, 0, 0); // 21:00 IST

    if (targetIst.getTime() <= nowIstMs) {
        targetIst.setDate(targetIst.getDate() + 1);
    }

    const targetUtcMs = targetIst.getTime() - IST_OFFSET_MIN * 60 * 1000;
    return new Date(targetUtcMs);
}

// (optional older helper, not used by daily draw anymore; keep if other code uses)
export function getNextBatchTime(minutes = 1440) {
    const now = new Date();
    const ms = minutes * 60 * 1000;
    return new Date(Math.ceil(now.getTime() / ms) * ms);
}

/* -------------------------------------------------------------------------- */
/*                               Card lifecycle                               */
/* -------------------------------------------------------------------------- */

// Get the current active card or create one for the next 9:00 PM IST batch
export async function getActiveLotteryCard() {
    // 1) Try to get an active card
    const qActive = query(
        collection(db, "lottery_cards"),
        where("status", "==", "active"),
        orderBy("createdAt", "desc"),
        limit(1)
    );
    const activeSnap = await getDocs(qActive);
    if (!activeSnap.empty) {
        const d = activeSnap.docs[0];
        return { id: d.id, ...d.data() };
    }

    // 2) No active card → try an existing pending batch to join
    const now = new Date();
    const qPendingBatch = query(
        collection(db, "lottery_cards"),
        where("status", "==", "waiting_draw"),
        where("batchDrawAt", ">", now),
        orderBy("batchDrawAt", "asc"),
        limit(1)
    );
    const pendingSnap = await getDocs(qPendingBatch);

    let batchDrawAt;
    if (!pendingSnap.empty) {
        batchDrawAt = pendingSnap.docs[0].data().batchDrawAt;
    } else {
        // 3) No pending batch → create a fresh one for the next 9:00 PM IST
        batchDrawAt = getNextISTDrawDate();
    }

    const newCard = {
        createdAt: serverTimestamp(),
        batchDrawAt,
        filled: [],
        owners: {},
        status: "active",
    };
    const docRef = await addDoc(collection(db, "lottery_cards"), newCard);
    const docSnap = await getDoc(docRef);
    return { ...newCard, id: docRef.id, ...(docSnap.data() || {}) };
}

// Listen to the current active card (top-most by createdAt)
export function listenToCard(setCard) {
    const q = query(
        collection(db, "lottery_cards"),
        where("status", "==", "active"),
        orderBy("createdAt", "desc"),
        limit(1)
    );
    const unsub = onSnapshot(q, (snap) => {
        if (!snap.empty) {
            const d = snap.docs[0];
            setCard({ id: d.id, ...d.data() });
        }
    });
    return unsub;
}

/**
 * Countdown listener to the *earliest* upcoming batch (active or waiting_draw).
 * If you switched to <CountdownTimer target={card?.batchDrawAt} /> you can remove this.
 */
export function listenToCountdown(setCountdown) {
    let interval = null;
    const q = query(
        collection(db, "lottery_cards"),
        where("status", "in", ["active", "waiting_draw"]),
        orderBy("batchDrawAt", "asc"),
        limit(1)
    );
    const unsub = onSnapshot(
        q,
        (snap) => {
            if (interval) clearInterval(interval);
            if (snap.empty) {
                setCountdown(0);
                return;
            }
            const data = snap.docs[0].data();
            const target =
                data.batchDrawAt?.toDate?.() ||
                (data.batchDrawAt ? new Date(data.batchDrawAt) : null);
            if (!target) {
                setCountdown(0);
                return;
            }
            interval = setInterval(() => {
                const secs = Math.max(
                    0,
                    Math.floor((target.getTime() - Date.now()) / 1000)
                );
                setCountdown(secs);
            }, 1000);
        },
        (err) => console.error("listenToCountdown error:", err)
    );
    return () => {
        unsub();
        if (interval) clearInterval(interval);
    };
}

// After a card fills to 100, park it for the current batch draw and open a fresh active card for *same* batch
export async function closeAndCreateNewCard(cardId) {
    const cardRef = doc(db, "lottery_cards", cardId);
    await runTransaction(db, async (t) => {
        const snap = await t.get(cardRef);
        if (!snap.exists()) throw new Error("Card not found");
        const card = snap.data();

        t.update(cardRef, { status: "waiting_draw", filledAt: new Date() });

        t.set(doc(collection(db, "lottery_cards")), {
            createdAt: serverTimestamp(),
            batchDrawAt: card.batchDrawAt, // keep same batch
            filled: [],
            owners: {},
            status: "active",
        });
    });
}

/* -------------------------------------------------------------------------- */
/*                                    Buy                                     */
/* -------------------------------------------------------------------------- */

// Race-safe, partial-success purchase. Returns { bought, rejected, charged }
export async function buyNumbers(cardId, userId, numbers) {
    const cardRef = doc(db, "lottery_cards", cardId);
    const userRef = doc(db, "users", userId);

    return await runTransaction(db, async (t) => {
        const cardSnap = await t.get(cardRef);
        if (!cardSnap.exists()) throw new Error("CardNotFound");
        const card = cardSnap.data();

        const userSnap = await t.get(userRef);
        if (!userSnap.exists()) throw new Error("UserNotFound");
        const user = userSnap.data();

        const owners = card.owners || {};
        const requested = numbers.map((n) => Number(n));
        const alreadyTaken = requested.filter((n) =>
            Object.prototype.hasOwnProperty.call(owners, String(n))
        );
        const available = requested.filter(
            (n) => !Object.prototype.hasOwnProperty.call(owners, String(n))
        );

        if (available.length === 0) {
            throw new Error("AllSelectedNumbersTaken");
        }

        const price = available.length * 0.5;
        const balance = user.balance ?? 0;
        if (balance < price) throw new Error("InsufficientFunds");

        // Update card
        const newOwners = { ...owners };
        available.forEach((n) => {
            newOwners[String(n)] = {
                uid: userId,
                username: user.username || userId,
            };
        });
        const newFilled = Array.from(
            new Set([...(card.filled || []), ...available])
        );

        t.update(cardRef, { owners: newOwners, filled: newFilled });
        t.update(userRef, { balance: balance - price });

        // History (only purchased numbers)
        t.set(doc(collection(db, "lottery_purchases")), {
            userId,
            username: user.username || userId,
            cardId,
            numbers: available,
            boughtAt: serverTimestamp(),
            batchDrawAt: card.batchDrawAt || null,
            won: false,
            amount: 0,
            resolved: false,
        });

        return { bought: available, rejected: alreadyTaken, charged: price };
    });
}

/* -------------------------------------------------------------------------- */
/*                                UI listeners                                */
/* -------------------------------------------------------------------------- */

export function listenToUserHistory(userId, setHistory) {
    if (!userId) return () => { };
    const q = query(
        collection(db, "lottery_purchases"),
        where("userId", "==", userId),
        orderBy("boughtAt", "desc"),
        limit(10)
    );
    return onSnapshot(q, (snap) => {
        setHistory(
            snap.docs.map((d) => ({
                ...d.data(),
                id: d.id,
            }))
        );
    });
}

export function listenToWinners(setWinners) {
    const q = query(
        collection(db, "lottery_winners"),
        orderBy("time", "desc"),
        limit(10)
    );
    return onSnapshot(q, (snap) => {
        setWinners(
            snap.docs.map((d) => ({
                ...d.data(),
                id: d.id,
                time: d.data().time?.toDate()?.toLocaleString() ?? "",
            }))
        );
    });
}

// Cards waiting for draw (for Pending panel)
export function listenToPendingCards(setCards) {
    const q = query(
        collection(db, "lottery_cards"),
        where("status", "==", "waiting_draw"),
        orderBy("batchDrawAt", "asc"),
        limit(10)
    );
    return onSnapshot(q, (snap) => {
        setCards(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
}

// Recently drawn cards (for "Recent Draws" panel)
export function listenToRecentDrawn(setCards) {
    const q = query(
        collection(db, "lottery_cards"),
        where("status", "==", "drawn"),
        orderBy("batchDrawAt", "desc"),
        limit(10)
    );
    return onSnapshot(q, (snap) => {
        setCards(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
}

/* -------------------------------------------------------------------------- */
/*                                Draw (client)                               */
/* -------------------------------------------------------------------------- */

// Client-side fallback (your backend cron now handles draws)
// Keep it safe by using string keys for owners.
export async function drawWinner(card) {
    if (!card || card.winner) return;
    const numbers = card.filled || [];
    if (numbers.length === 0) return;

    const winnerNumber = numbers[Math.floor(Math.random() * numbers.length)];
    const winnerInfo = (card.owners || {})[String(winnerNumber)];
    if (!winnerInfo) return;

    const prize = numbers.length * 0.5 * 0.8; // 80% to winner
    const cardRef = doc(db, "lottery_cards", card.id);
    const userRef = doc(db, "users", winnerInfo.uid);

    const userSnap = await getDoc(userRef);
    const user = userSnap.data() || {};

    await updateDoc(cardRef, {
        winner: {
            uid: winnerInfo.uid,
            username: winnerInfo.username,
            number: winnerNumber,
            amount: prize,
            drawAt: new Date(),
        },
        status: "drawn",
    });

    await addDoc(collection(db, "lottery_winners"), {
        username: winnerInfo.username,
        number: winnerNumber,
        amount: prize,
        time: new Date(),
        cardId: card.id,
    });

    await updateDoc(userRef, {
        balance: (user.balance ?? 0) + prize,
    });

    const purchasesQ = query(
        collection(db, "lottery_purchases"),
        where("userId", "==", winnerInfo.uid),
        where("cardId", "==", card.id)
    );
    const purchasesSnap = await getDocs(purchasesQ);
    purchasesSnap.forEach((docSnap) => {
        const nums = (docSnap.data().numbers || []).map(Number);
        if (nums.includes(winnerNumber)) {
            updateDoc(doc(db, "lottery_purchases", docSnap.id), {
                won: true,
                amount: prize,
            });
        }
    });
}

/* -------------------------------------------------------------------------- */
/*                        Detail viewers / admin helpers                       */
/* -------------------------------------------------------------------------- */

export async function getCardDetails(cardId) {
    const ref = doc(db, "lottery_cards", cardId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Card not found");
    const card = { id: snap.id, ...snap.data() };

    const ownersMap = card.owners || {};
    const entries = Object.entries(ownersMap).map(([num, info]) => ({
        number: Number(num),
        uid: info.uid,
        username: info.username || info.uid,
    }));
    entries.sort((a, b) => a.number - b.number);

    const byUser = {};
    for (const e of entries) {
        if (!byUser[e.uid])
            byUser[e.uid] = { uid: e.uid, username: e.username, count: 0, numbers: [] };
        byUser[e.uid].count += 1;
        byUser[e.uid].numbers.push(e.number);
    }
    const users = Object.values(byUser).sort((a, b) => b.count - a.count);

    return { card, entries, users };
}

export async function getPurchasesForCard(cardId) {
    const q = query(
        collection(db, "lottery_purchases"),
        where("cardId", "==", cardId)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/* ------------------------------ one-time fix ------------------------------- */
// If a card was created with a wrong time, call this once to retime it.
export async function retimeActiveCardToIST9PM(cardId) {
    await updateDoc(doc(db, "lottery_cards", cardId), {
        batchDrawAt: getNextISTDrawDate(),
    });
}
